import { config, gameState, resetGameState } from '@js/state';
import { SPACE_BG, addStarfields } from '@utils/space';

let rewardTriggered = false;

export default class EndScene extends Phaser.Scene {
  constructor() {
    super({ key: 'EndScene' });
  }

  create() {
    rewardTriggered = false;
    const { width: W, height: H } = config;
    this.cameras.main.setBackgroundColor(SPACE_BG);
    this.cameras.main.fadeIn(400, 0, 0, 0);

    addStarfields(this);

    // Dim wash so the spotlight reads.
    this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.55);

    // Spotlight beam from the top — the cone PNG is black with an alpha
    // gradient (opaque top → transparent bottom); on artemis's dark background
    // a straight draw is invisible, so tint-fill white + ADD blend turns it
    // into a luminous beam.
    this.spotlightCone = this.add
      .image(W / 2, 0, 'spotlight-cone')
      .setOrigin(0.5, 0)
      .setDisplaySize(720, 717)
      .setTintFill(0xffffff)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0);
    this.tweens.add({
      targets: this.spotlightCone,
      alpha: 0.7,
      duration: 1500,
      delay: 600,
      ease: 'Sine.easeOut',
    });

    if (gameState.gameOver) {
      this.buildGameOver(W, H);
    } else {
      this.buildReward(W, H);
    }
  }

  buildReward(W, H) {
    const midWidth = W / 2;
    const midHeight = H / 2;
    this.yOffset = 75;
    // The helmet sits roughly centred in its sprite, so orbit the head a bit above centre.
    const HEAD_OFFSET_Y = 30;
    this.centerxOfCircle = midWidth;
    this.centeryOfCircle = (midHeight - this.yOffset) - HEAD_OFFSET_Y;
    this.rewardAppearDelay = 1300;
    this.rewardMotionDuration = 1350;

    this.baseCircle = this.add
      .sprite(midWidth, midHeight, 'baseCircle')
      .setOrigin(0.5)
      .setScale(0.4);

    this.rewardLight = this.add
      .sprite(midWidth, midHeight, 'rewardLight')
      .setOrigin(0.5)
      .setScale(0.4);

    this.reward = this.add
      .image(midWidth, midHeight + 130, 'reward-helmet')
      .setOrigin(0.5)
      .setVisible(false)
      .setAngle(300);
    this.rewardTargetScale = 1.15;
    this.reward.setScale(this.rewardTargetScale * 0.3);

    this.glitter = this.add
      .sprite(midWidth + 30, midHeight - 65, 'glitter')
      .setOrigin(0.5)
      .setVisible(false)
      .setScale(0.8);

    this.path = { t: 0, vec: new Phaser.Math.Vector2() };
    this.curve = new Phaser.Curves.Ellipse(
      this.centerxOfCircle,
      this.centeryOfCircle,
      40,
      40,
    );
    this.tweens.add({
      targets: this.path,
      t: 1,
      ease: 'Sine.easeInOut',
      duration: 5400,
      yoyo: true,
      repeat: -1,
    });

    this.time.delayedCall(this.rewardAppearDelay, () => this.reward.setVisible(true));
    this.tweens.add({
      targets: this.reward,
      x: midWidth,
      y: midHeight - this.yOffset,
      angle: 0,
      scaleX: this.rewardTargetScale,
      scaleY: this.rewardTargetScale,
      delay: this.rewardAppearDelay,
      ease: 'Back.easeOut',
      duration: 1000,
      onComplete: () => {
        this.glitter.setVisible(true).play('showglitter');
        rewardTriggered = true;

        // Glitter orbit centre moves down/up too, so the orbit follows the helmet's bob.
        this.tweens.add({
          targets: this.curve.p0,
          y: this.centeryOfCircle + 60,
          ease: 'Sine.easeInOut',
          duration: this.rewardMotionDuration,
          yoyo: true,
          repeat: -1,
        });
        this.tweens.add({
          targets: this.reward,
          y: { from: midHeight - this.yOffset, to: midHeight - this.yOffset + 60 },
          ease: 'Sine.easeInOut',
          duration: this.rewardMotionDuration,
          yoyo: true,
          repeat: -1,
        });

        // Canonical End screen (Figma 4:49600): "Rewards earned!" centred at
        // (640,148), Untitled Sans Regular 16, white @ 70%.
        this.add
          .text(midWidth, 148, 'Rewards earned!', {
            fontFamily: 'UntitledSans-Regular', fontSize: '16px', color: '#ffffff',
          })
          .setOrigin(0.5)
          .setAlpha(0.7);
        this.add
          .text(midWidth, midHeight + 215, 'OMEGA HELMET', {
            fontFamily: 'Athletics-Bold', fontSize: '24px', color: '#ffd27a',
          })
          .setOrigin(0.5);
        this.buildPlayAgain(midWidth, 640, 300);
      },
    });

    if (!this.anims.exists('showglitter')) {
      this.anims.create({
        key: 'showglitter',
        frames: this.anims.generateFrameNames('glitter', {
          start: 0, end: 32, prefix: 'glitterAnim', suffix: '.png',
        }),
        frameRate: 15, repeat: -1,
      });
    }
    if (!this.anims.exists('circleopen')) {
      this.anims.create({
        key: 'circleopen',
        frames: this.anims.generateFrameNames('baseCircle', {
          start: 0, end: 74, prefix: 'baseCircleAnim', suffix: '.png',
        }),
        frameRate: 30, repeat: 0,
      });
    }
    if (!this.anims.exists('circleglitterloop')) {
      this.anims.create({
        key: 'circleglitterloop',
        frames: this.anims.generateFrameNames('baseCircle', {
          start: 74, end: 155, prefix: 'baseCircleAnim', suffix: '.png',
        }),
        frameRate: 30, repeat: -1,
      });
    }
    if (!this.anims.exists('rewardopen')) {
      this.anims.create({
        key: 'rewardopen',
        frames: this.anims.generateFrameNames('rewardLight', {
          start: 0, end: 155, prefix: 'rewardLightAnim', suffix: '.png',
        }),
        frameRate: 30, repeat: 0,
      });
    }

    this.baseCircle.play('circleopen').once('animationcomplete', () => {
      this.baseCircle.play('circleglitterloop');
    });
    this.rewardLight.play('rewardopen');
  }

  update() {
    if (rewardTriggered && this.glitter && this.curve) {
      this.curve.getPoint(this.path.t, this.path.vec);
      this.glitter.setPosition(this.path.vec.x, this.path.vec.y);
    }
  }

  buildGameOver(W, H) {
    this.add
      .text(W / 2, H / 2 - 40, 'Game Over', {
        fontFamily: 'Athletics-Regular',
        fontSize: '60px',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    this.buildPlayAgain(W / 2, H / 2 + 80, 300);
  }

  buildPlayAgain(x, y, delay) {
    // ─── Pre-flight ───
    // Inputs: pointerdown/up on the Play-again sprite.
    // State mutated: disableInteractive() then replay() fires scene.start (one-shot).
    // Re-entry guards: disableInteractive on first pointerup, before replay.
    // Cleanup: scene shutdown destroys the sprite.
    // Manually verified: [x] double click [x] click during fade-in [x] rapid clicks
    const btn = this.add
      .image(x, y, 'play-again-button')
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setAlpha(0);
    this.tweens.add({ targets: btn, alpha: 1, duration: 400, delay });

    btn.on('pointerover', () => btn.setScale(1.04));
    btn.on('pointerout', () => btn.setScale(1));
    btn.on('pointerdown', () => btn.setScale(0.97));
    btn.on('pointerup', () => {
      btn.disableInteractive();
      btn.setScale(1);
      this.replay();
    });
  }

  replay() {
    resetGameState();
    this.cameras.main.fadeOut(250, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('StartScene');
    });
  }
}
